import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import * as XLSX from "xlsx";

const C = {
  navy: "#4A4031", gold: "#6B8E4E", lightGold: "#EDF2E4",
  green: "#2E7D4F", red: "#C0392B", orange: "#E67E22",
  white: "#FFFFFF", lightGray: "#F7F7F7", border: "#E0E0E0",
  text: "#2C2C2C", subtext: "#666",
};

const card = { background: C.white, borderRadius: 12, padding: 20, border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 20 };
const titleStyle = { color: C.navy, fontSize: 15, fontWeight: "bold", marginBottom: 14, paddingBottom: 8, borderBottom: `2px solid ${C.gold}`, display: "flex", justifyContent: "space-between", alignItems: "center" };

const monthlyData = [
  { month: "ינואר", החודש: 98000, "חודש קודם": 85000 },
  { month: "פברואר", החודש: 110000, "חודש קודם": 98000 },
  { month: "מרץ", החודש: 95000, "חודש קודם": 110000 },
  { month: "אפריל", החודש: 120000, "חודש קודם": 95000 },
  { month: "מאי", החודש: 115000, "חודש קודם": 120000 },
  { month: "יוני", החודש: 125000, "חודש קודם": 115000 },
];

function downloadCSV(data, filename, columns) {
  const header = columns.map(c => c.label);
  const body = data.map(item => columns.map(c => item[c.key] ?? ""));
  const aoa = [header, ...body];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  const xlsxName = filename.replace(/\.csv$/i, "") + ".xlsx";
  XLSX.writeFile(wb, xlsxName);
  return;
  // legacy CSV (unreachable)
  const a = document.createElement("a");
  a.download = filename;
  a.click();
}

const reorderCols = [
  { key: "code", label: "קוד" }, { key: "name", label: "שם מוצר" },
  { key: "subgroup", label: "תת קבוצה" }, { key: "stock", label: "מלאי" },
  { key: "daily_sales", label: "מכירות יומיות" }, { key: "days_left", label: "ימים עד גמר" },
  { key: "recommended_qty", label: "כמות מומלצת" },
  { key: "unit_cost", label: "עלות ליח' (₪)" },
  { key: "spike_buffer", label: "באפר spike" },
];

const deadStockCols = [
  { key: "ItemCode", label: "קוד" }, { key: "ItemName", label: "שם מוצר" },
  { key: "U_SubGroup", label: "תת קבוצה" }, { key: "OnHand", label: "מלאי" },
  { key: "AvgPrice", label: "מחיר עלות" }, { key: "StockValue", label: "שווי" },
  { key: "SoldLast6M", label: "נמכר 6 חודשים" }, { key: "DaysSinceLastSale", label: "ימים ללא מכירה" },
];

function ExcelBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{ background: C.gold, color: C.navy, border: "none", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: "bold" }}>
      הורד Excel
    </button>
  );
}

function SubgroupSection({ sectionTitle, data, color, columns, noOrderLabel }) {
  const [openCats, setOpenCats] = useState({});      // categories start CLOSED
  const [hideOrderedShipped, setHideOrderedShipped] = useState(false);
  const [hideOrderedNotShipped, setHideOrderedNotShipped] = useState(false);
  const [sortBy, setSortBy] = useState("days");      // "days" | "code"
  const [search, setSearch] = useState("");
  const subgroups = Object.keys(data || {}).sort((a, b) => {
    const na = (data[a] && data[a][0] && data[a][0].subgroup_name) || a;
    const nb = (data[b] && data[b][0] && data[b][0].subgroup_name) || b;
    return String(na).localeCompare(String(nb), "he");
  });
  if (subgroups.length === 0) return <div style={card}><div style={titleStyle}><span>{sectionTitle}</span></div><p style={{ color: C.subtext }}>אין נתונים</p></div>;
  const allItems = subgroups.flatMap(sg => data[sg]);

  // filter + sort a category's items
  const prep = (items) => {
    let r = items;
    // "shipped" = has a PO in SAP (incoming). A PO means the factory confirmed + shipped.
    // hide "ordered AND shipped" = anything that has a PO
    if (hideOrderedShipped) r = r.filter(it => !it.incoming);
    // hide "ordered but NOT shipped" = has proforma (PI) but no PO yet
    if (hideOrderedNotShipped) r = r.filter(it => !(it.pi_ordered && !it.incoming));
    if (search.trim()) r = r.filter(it => String(it.code).includes(search.trim()));
    r = [...r].sort((a, b) =>
      sortBy === "code"
        ? String(a.code).localeCompare(String(b.code), undefined, { numeric: true })
        : (a.days_left || 1e9) - (b.days_left || 1e9)
    );
    return r;
  };

  const btn = (active) => ({
    background: active ? C.navy : "#fff", color: active ? "#fff" : C.navy,
    border: `1px solid ${C.navy}`, borderRadius: 6, padding: "4px 10px",
    cursor: "pointer", fontSize: 12, fontWeight: "bold",
  });

  return (
    <div style={card}>
      <div style={titleStyle}>
        <span>{sectionTitle}</span>
        <ExcelBtn onClick={() => downloadCSV(allItems, `${sectionTitle}.csv`, columns)} />
      </div>

      {/* sticky controls bar - stays visible while scrolling (mobile-friendly) */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, background: "#fff",
        borderBottom: `1px solid ${C.border}`, padding: "8px 4px", display: "flex",
        flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="חיפוש מק״ט" inputMode="numeric"
          style={{ padding: "4px 8px", border: `1px solid ${C.border}`, borderRadius: 6,
            fontSize: 12, width: 110 }} />
        <button style={btn(hideOrderedShipped)} onClick={() => setHideOrderedShipped(v => !v)}>הסתר הוזמן ונשלח</button>
        <button style={btn(hideOrderedNotShipped)} onClick={() => setHideOrderedNotShipped(v => !v)}>הסתר הוזמן אך לא נשלח</button>
        <span style={{ color: C.subtext, fontSize: 12 }}>מיון:</span>
        <button style={btn(sortBy === "days")} onClick={() => setSortBy("days")}>דחיפות</button>
        <button style={btn(sortBy === "code")} onClick={() => setSortBy("code")}>מק״ט</button>
      </div>

      <div style={{ marginTop: 12 }}>
      {subgroups.map(sg => {
        const items = prep(data[sg]);
        const sgName = (data[sg][0] && data[sg][0].subgroup_name) || sg;
        const isOpen = openCats[sg];
        if (search.trim() && items.length === 0) return null;  // hide empty cats during search
        return (
          <div key={sg} style={{ marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            <div onClick={() => setOpenCats(p => ({ ...p, [sg]: !isOpen }))}
              style={{ background: C.lightGray, padding: "8px 14px", display: "flex",
                justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
              <span style={{ fontWeight: "bold", color: C.navy }}>
                {isOpen ? "▼" : "◀"} {sgName} ({sg})
              </span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                <span style={{ color, fontSize: 13, fontWeight: "bold" }}>{items.length} מוצרים</span>
                <ExcelBtn onClick={() => downloadCSV(items, `${sgName} (${sg}).csv`, columns)} />
              </div>
            </div>
            {isOpen && (
            <div style={{ padding: "6px 14px" }}>
              {items.map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 13, flexWrap: "wrap", gap: 4 }}>
                  <span style={{ fontWeight: "bold", color: C.text, minWidth: 70 }} title={item.cny_early ? "הזמנה מוקדמת - המפעל סגור בראש השנה הסיני" : undefined}>{item.is_final ? "🔫 " : ""}{item.cny_early ? "🎎 " : ""}{item.code}</span>
                  <span style={{ color: C.subtext, flex: 1, minWidth: 120 }}>{item.name}</span>
                  <span style={{ color: C.subtext }}>{(item.stock || 0).toLocaleString()} יח'</span>
                  {item.days_left && <span style={{ color, fontWeight: "bold" }}>נגמר בעוד {item.days_left} ימים</span>}
                  {noOrderLabel
                    ? <span style={{ color: C.navy, fontWeight: "bold" }}>אל תזמין</span>
                    : <span style={{ color: C.navy }}>הזמן: {(item.recommended_qty || 0).toLocaleString()} יח'</span>}
                  {item.pack_qty > 1 && <span style={{ color: C.subtext, fontSize: 12 }}>מארז: {item.pack_qty.toLocaleString()} יח'</span>}
                  <span style={{ color: C.subtext, fontSize: 12 }}>כניסה אחרונה: {item.last_receipt || "אין נתונים"}</span>
                  {item.incoming && (
                    <span style={{ width: "100%", color: "#1B7A3D", fontWeight: "bold", fontSize: 12 }}>
                      🚚 כבר בדרך: {(item.incoming.open_qty || 0).toLocaleString()} יח' {(item.incoming.orders || []).length > 1
                        ? "— " + item.incoming.orders.map(o => `הזמנה ${o.doc_num}: ${(o.open_qty || 0).toLocaleString()} יח' (${o.date})`).join(" | ")
                        : `(הזמנה #${item.incoming.doc_num}, ${item.incoming.date})`}
                    </span>
                  )}
                  {item.pi_ordered && (
                    <span style={{ width: "100%", color: C.navy, fontWeight: "bold", fontSize: 12 }}>
                      🧾 כבר הוזמן: {(item.pi_ordered.open_pis || []).length > 1
                        ? item.pi_ordered.open_pis.map(p => `${(p.qty || 0).toLocaleString()} יח' (${p.pi_number}, ${p.pi_date})`).join(" + ")
                        : `${(item.pi_ordered.pi_qty || 0).toLocaleString()} יח' (פרופורמה ${item.pi_ordered.pi_number}, ${item.pi_ordered.pi_date})`}
                    </span>
                  )}
                  {item.qty_mismatch && (
                    <span style={{ width: "100%", color: "#B5651D", fontWeight: "bold", fontSize: 12 }}>
                      ⚠️ פער בכמות: המפעל אישר {Math.abs(item.qty_mismatch.diff_pct)}% {item.qty_mismatch.diff_pct < 0 ? "פחות" : "יותר"} מההזמנה{item.qty_mismatch.pi_number ? ` (${item.qty_mismatch.pi_number})` : ''} ({(item.qty_mismatch.po_qty || 0).toLocaleString()} מול {(item.qty_mismatch.pi_qty || 0).toLocaleString()} יח')
                    </span>
                  )}
                </div>
              ))}
              {items.length === 0 && <p style={{ color: C.subtext, fontSize: 13 }}>אין מוצרים (לפי הסינון)</p>}
            </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}

function SpikeSection({ data }) {
  const [expandedSg, setExpandedSg] = useState({});
  const [expandedProduct, setExpandedProduct] = useState({});
  const [spikeSearch, setSpikeSearch] = useState("");
  const subgroups = Object.keys(data || {}).sort((a, b) => {
    const na = (data[a] && data[a][0] && data[a][0].subgroup_name) || a;
    const nb = (data[b] && data[b][0] && data[b][0].subgroup_name) || b;
    return String(na).localeCompare(String(nb), "he");
  });
  if (subgroups.length === 0) return <div style={card}><p style={{ color: C.subtext }}>אין אירועי spike בשנה האחרונה</p></div>;

  const allRows = subgroups.flatMap(sg =>
    data[sg].flatMap(p => p.spikes.map(s => ({
      subgroup: sg, code: p.code, name: p.name,
      date: s.date, qty: s.qty, avg_qty: s.avg_qty, multiplier: s.multiplier
    })))
  );

  const spikeCols = [
    { key: "subgroup", label: "קטגוריה" }, { key: "code", label: "קוד" },
    { key: "name", label: "שם מוצר" }, { key: "date", label: "תאריך" },
    { key: "qty", label: "כמות spike" }, { key: "avg_qty", label: "ממוצע רגיל" },
    { key: "multiplier", label: "פי כמה" },
  ];

  return (
    <div style={card}>
      <div style={titleStyle}>
        <span>היסטוריית Spike לפי קטגוריה</span>
        <ExcelBtn onClick={() => downloadCSV(allRows, "spikes.csv", spikeCols)} />
      </div>
      <input value={spikeSearch} onChange={e => setSpikeSearch(e.target.value)}
        placeholder="חיפוש מק״ט או שם מוצר"
        style={{ width: "100%", padding: "9px 12px", fontSize: 14, border: "1px solid #E0E0E0", borderRadius: 8, marginBottom: 14, boxSizing: "border-box" }} />
      {subgroups.map(sg => {
        const q = spikeSearch.trim().toLowerCase();
        const products = q
          ? data[sg].filter(p => String(p.code || "").toLowerCase().includes(q) || String(p.name || "").toLowerCase().includes(q))
          : data[sg];
        if (products.length === 0) return null;
        const sgName = (products[0] && products[0].subgroup_name) || sg;
        const totalSpikes = products.reduce((sum, p) => sum + p.spikes.length, 0);
        const isSgExpanded = expandedSg[sg];
        const displayProducts = isSgExpanded ? products : products.slice(0, 3);

        return (
          <div key={sg} style={{ marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            <div style={{ background: C.lightGray, padding: "8px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: "bold", color: C.navy }}>{sgName} ({sg})</span>
              <span style={{ color: C.orange, fontSize: 13, fontWeight: "bold" }}>{products.length} מוצרים | {totalSpikes} אירועים</span>
            </div>
            <div style={{ padding: "6px 14px" }}>
              {displayProducts.map((product, pi) => {
                const productKey = `${sg}-${product.code}`;
                const isProductExpanded = expandedProduct[productKey];
                const displaySpikes = isProductExpanded ? product.spikes : product.spikes.slice(0, 2);

                return (
                  <div key={pi} style={{ marginBottom: 10, padding: "8px", background: "#FAFAFA", borderRadius: 6, border: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontWeight: "bold", color: C.text }}>{product.code} – {product.name}</span>
                      <span style={{ color: C.orange, fontSize: 12, fontWeight: "bold" }}>{product.spikes.length} spikes</span>
                    </div>
                    {displaySpikes.map((spike, si) => (
                      <div key={si} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", fontSize: 12, background: C.white, borderRadius: 4, marginBottom: 3 }}>
                        <span style={{ color: C.subtext }}>{spike.date}</span>
                        <span style={{ color: C.orange, fontWeight: "bold" }}>{(spike.qty || 0).toLocaleString()} יח'</span>
                        <span style={{ color: C.subtext }}>ממוצע: {(spike.avg_qty || 0).toLocaleString()}</span>
                        <span style={{ color: C.red, fontWeight: "bold" }}>פי {spike.multiplier}</span>
                      </div>
                    ))}
                    {product.spikes.length > 2 && (
                      <button onClick={() => setExpandedProduct(p => ({ ...p, [productKey]: !isProductExpanded }))} style={{ background: "none", border: "none", color: C.navy, cursor: "pointer", fontSize: 12, padding: "2px 0" }}>
                        {isProductExpanded ? "הצג פחות" : `הצג עוד ${product.spikes.length - 2} spikes`}
                      </button>
                    )}
                  </div>
                );
              })}
              {products.length > 3 && (
                <button onClick={() => setExpandedSg(p => ({ ...p, [sg]: !isSgExpanded }))} style={{ background: "none", border: "none", color: C.navy, cursor: "pointer", fontSize: 13, padding: "6px 0" }}>
                  {isSgExpanded ? "הצג פחות" : `הצג עוד ${products.length - 3} מוצרים`}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const TAB_INFO = {
  overview: "מבט-על על המספרים החשובים: הכנסה נטו ב-30 יום, יחידות שנמכרו, וצמיחה לעומת 30 הימים הקודמים. למטה - 50 המוצרים המובילים לפי מחזור ב-30 יום, ומתחתיהם מכירות לפי תת-קבוצה מתחילת השנה הקלנדרית, עם 10 המוצרים החזקים בכל תת-קבוצה. שימו לב: הטופ 50 מחושב על 30 יום אחרונים, ותת-הקבוצות מתחילת השנה - לכן המספרים שונים.",
  order_now: "מוצרים שייגמרו תוך פחות מ-120 יום ונמכרים טוב - כדאי להזמין עכשיו. הכמות המומלצת מחושבת לפי קצב המכירה היומי ב-90 הימים האחרונים.",
  order_soon: "מוצרים שייגמרו בעוד 120 עד 150 יום - התראה מוקדמת כדי שתספיק להזמין בזמן, לפני שהמלאי נגמר.",
  no_order: "מוצרים שעומדים להיגמר אבל הם תקועים (נמכרים לאט) או שהופסקו - עדיף לתת להם להיגמר ולא להזמין עוד.",
  slow_moving: "מלאי תקוע: מוצרים שמכרו פחות מ-10% מהמלאי בחצי השנה האחרונה. כמעט לא זזים. השווי מחושב לפי מחיר הרכישה האחרון.",
  slow_movers: "מוצר איטי: מוצרים שמכרו פחות מ-25% מהמלאי ברבעון האחרון. זזים לאט. (מוצר תקוע לא מופיע כאן - הוא מופיע רק במלאי תקוע).",
  spikes: "אירועי spike: הזמנה בודדת חריגה - גדולה פי 10 או יותר מהממוצע השנתי של אותו מוצר. מראה לך מתי היתה קפיצה לא רגילה.",
};

function ListControls({ search, setSearch, sortBy, setSortBy, sortOptions }) {
  const sbtn = (on) => ({ padding: "8px 12px", borderRadius: 8, border: "1px solid #4A4031",
    background: on ? "#4A4031" : "#fff", color: on ? "#fff" : "#4A4031",
    fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" });
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="חיפוש מק״ט או שם"
        style={{ flex: 1, minWidth: 140, padding: "9px 12px", fontSize: 14, border: "1px solid #E0E0E0", borderRadius: 8, boxSizing: "border-box" }} />
      <span style={{ fontSize: 13, color: "#666", fontWeight: 600 }}>מיין:</span>
      {sortOptions.map(o => (
        <button key={o.key} style={sbtn(sortBy === o.key)} onClick={() => setSortBy(o.key)}>{o.label}</button>
      ))}
    </div>
  );
}


function GrowthOpportunities({ items, onMark, busy, leaving }) {
  const reduce = typeof window !== "undefined" && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // Pure view. The list and the marking live once in Dashboard - one source of
  // truth, so a mark in any tab is reflected on every screen.
  const [open, setOpen] = useState(false);
  const mark = (it, m) => onMark(it, m);
  if (items === "error") return (
    <div style={{ background: "#FEF2F2", border: "1px solid #B0451C", borderRadius: 12,
                  padding: "8px 14px", marginBottom: 14, color: "#B0451C", fontSize: 13 }}>
      לא ניתן לטעון תובנות
    </div>);
  if (!items || items.length === 0) return null;
  const btn = (bg) => ({ padding: "5px 10px", borderRadius: 7, border: "none",
    background: bg, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" });
  return (
    <div style={{ background: "#FFF8E7", border: "1px solid #E5C76B",
                  borderRadius: 12, padding: "10px 14px", marginBottom: 14 }}>
      <div onClick={() => setOpen(o => !o)}
           style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <span style={{ fontSize: 16 }}>💡</span>
        <span style={{ fontWeight: "bold", color: "#7A5C00" }}>
          {items.length} תובנות - מוצרים בצמיחה שהמלאי שלהם אוזל
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: "#7A5C00" }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (() => {
        // Group by subgroup: Amir orders per category, so seeing "this category
        // needs four items" is what lets him close one order. Group order follows
        // the strongest item in each, so the value ranking is preserved.
        const groups = [], idx = {};
        items.forEach(it => {
          const k = it.subgroup || "-";
          if (idx[k] === undefined) { idx[k] = groups.length; groups.push({ k, name: it.subgroup_name, items: [] }); }
          groups[idx[k]].items.push(it);
        });
        return groups.map(g => (
          <div key={g.k}>
            {g.items.length > 1 && (
              <div style={{ borderTop: "1px solid #E5C76B", paddingTop: 8, marginTop: 4,
                            fontWeight: "bold", color: "#7A5C00", fontSize: 13 }}>
                {g.name} - {g.items.length} מוצרים
              </div>
            )}
            {g.items.map(it => (
        <div key={it.code} style={{ borderTop: "1px solid #E5C76B", padding: "10px 0",
                                    fontSize: 13, lineHeight: 1.7,
                                    opacity: leaving === it.code ? 0 : 1,
                                    transform: leaving === it.code ? "translateX(16px)" : "none",
                                    transition: reduce ? "none"
                                      : "opacity 150ms ease-in, transform 150ms ease-in" }}>
          <div style={{ fontWeight: "bold" }}>{it.code} - {it.name}</div>
          <div style={{ color: "#6b6b6b" }}>
            {it.subgroup_name} | ברבעון המקביל אשתקד נמכרו {it.sold_last_year.toLocaleString()} יח',
            ברבעון האחרון {it.sold_now.toLocaleString()} יח'
          </div>
          <div style={{ color: "#6b6b6b" }}>
            מלאי {it.stock.toLocaleString()} יח'
            {it.incoming ? ` | בדרך ${it.incoming.toLocaleString()}` : ""}
            {it.pi_ordered ? ` | בפרופורמה ${it.pi_ordered.toLocaleString()}` : ""}
            {" "}= {it.days_left} ימים
          </div>
          <div style={{ fontWeight: "bold", color: "#7A5C00" }}>
            צריך להזמין: {it.need.toLocaleString()} יח'
            {it.pack_qty > 1 ? ` (מארז ${it.pack_qty.toLocaleString()})` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button disabled={busy === it.code} onClick={() => mark(it, "ordered")}
                    style={btn("#6B8E4E")}>הזמנתי</button>
            <button disabled={busy === it.code} onClick={() => mark(it, "later")}
                    style={btn("#8a8a8a")}>לא עכשיו</button>
            <button disabled={busy === it.code} onClick={() => mark(it, "not_relevant")}
                    style={btn("#B0451C")}>לא רלוונטי</button>
          </div>
        </div>
            ))}
          </div>
        ));
      })()}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [topSearch, setTopSearch] = useState("");
  const [gSearch, setGSearch] = useState("");
  const [gResults, setGResults] = useState([]);
  const [gProduct, setGProduct] = useState(null);
  const [gLoading, setGLoading] = useState(false);
  const gSeq = useRef(0);
  useEffect(() => {
    const term = gSearch.trim();
    setGProduct(null);
    if (term.length < 2) { setGResults([]); return; }
    const seq = ++gSeq.current;
    const t = setTimeout(() => {
      const pw = sessionStorage.getItem("dekel_pw");
      axios.get("https://bi.dekelltd.com/api/search", {
        params: { q: term },
        headers: { "X-Dashboard-Password": pw },
      }).then(res => {
        if (seq === gSeq.current) setGResults(res.data?.results || []);
      }).catch(() => { if (seq === gSeq.current) setGResults([]); });
    }, 300);
    return () => clearTimeout(t);
  }, [gSearch]);
  const loadProduct = (code) => {
    const pw = sessionStorage.getItem("dekel_pw");
    setGLoading(true); setGResults([]); setGSearch("");
    axios.get(`https://bi.dekelltd.com/api/product/${encodeURIComponent(code)}`, {
      headers: { "X-Dashboard-Password": pw },
    }).then(res => setGProduct(res.data))
      .catch(() => setGProduct({ found: false }))
      .finally(() => setGLoading(false));
  };
  const [deadSearch, setDeadSearch] = useState("");
  const [deadSort, setDeadSort] = useState("value");   // value | code | stock
  const [slowSearch, setSlowSearch] = useState("");
  const [slowSort, setSlowSort] = useState("stock");   // stock | code | quarter
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("dekel_pw"));
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");

  const [sgData, setSgData] = useState(null);
  const loadSubgroups = (pw) => {
    axios.get("https://bi.dekelltd.com/api/sales-by-subgroup", { headers: { "X-Dashboard-Password": pw } })
      .then(res => setSgData(res.data.groups || []))
      .catch(() => setSgData([]));
  };
  const loadDashboard = (pw) => {
    setLoading(true);
    loadSubgroups(pw);
    axios.get("https://bi.dekelltd.com/api/dashboard", { headers: { "X-Dashboard-Password": pw } })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => {
        setLoading(false);
        if (err.response && err.response.status === 401) {
          sessionStorage.removeItem("dekel_pw");
          setAuthed(false);
          setPwError("סיסמה שגויה");
        }
      });
  };

  useEffect(() => {
    const pw = sessionStorage.getItem("dekel_pw");
    if (pw) loadDashboard(pw);
    else setLoading(false);
  }, []);

  const [refreshingOrders, setRefreshingOrders] = useState(false);
  const [ordersBanner, setOrdersBanner] = useState(null);
  const [growthItems, setGrowthItems] = useState(null);
  const [growthBusy, setGrowthBusy] = useState(null);
  const [growthLeaving, setGrowthLeaving] = useState(null);
  useEffect(() => {
    if (!authed) return;
    axios.get("https://bi.dekelltd.com/api/growth-opportunities",
      { headers: { "X-Dashboard-Password": sessionStorage.getItem("dekel_pw") || "" } })
      .then(r => setGrowthItems(r.data.items || []))
      .catch(e => { console.log("growth-opportunities failed:", e); setGrowthItems("error"); });
  }, [authed]);
  const markGrowth = (it, m) => {
    setGrowthBusy(it.code);
    axios.post("https://bi.dekelltd.com/api/mark-recommendation",
      { item_code: it.code, mark: m, snapshot: it },
      { headers: { "X-Dashboard-Password": sessionStorage.getItem("dekel_pw") || "" } })
      .then(() => {
        // Fade the row out before removing it - the click needs visible feedback.
        setGrowthLeaving(it.code);
        setTimeout(() => {
          setGrowthItems(p => p.filter(x => x.code !== it.code));
          setGrowthLeaving(null);
        }, 160);
      })
      .finally(() => setGrowthBusy(null));
  };
  const [closingPi, setClosingPi] = useState(null);
  const handleConfirmClosure = (piNumber) => {
    const pw = sessionStorage.getItem("dekel_pw");
    if (!pw || closingPi) return;
    const ok = window.confirm(
      `לאשר שההזמנה ${piNumber} הגיעה?\n\nהפרופורמה תיסגר ולא תופיע יותר בהתראות ובמוצרים.`
    );
    if (!ok) return;
    setClosingPi(piNumber);
    axios.post("https://bi.dekelltd.com/api/confirm-closure",
      { proforma: piNumber },
      { headers: { "X-Dashboard-Password": pw } })
      .then(res => {
        setOrdersBanner(`ההזמנה ${piNumber} נסגרה (${res.data?.count || 0} מק"טים)`);
        loadDashboard(pw);
        setTimeout(() => setOrdersBanner(null), 8000);
      })
      .catch(() => {
        setOrdersBanner("שגיאה בסגירת ההזמנה - נסה שוב");
        setTimeout(() => setOrdersBanner(null), 8000);
      })
      .finally(() => setClosingPi(null));
  };

  const handleRefreshOrders = () => {
    const pw = sessionStorage.getItem("dekel_pw");
    if (!pw || refreshingOrders) return;
    setRefreshingOrders(true);
    setOrdersBanner(null);
    axios.post("https://bi.dekelltd.com/api/refresh-orders", {}, { headers: { "X-Dashboard-Password": pw } })
      .then(res => {
        const filesOk = res.data?.files_ok;
        setOrdersBanner(filesOk != null ? `ההזמנות עודכנו - ${filesOk} קבצים נקראו` : "ההזמנות עודכנו");
        loadDashboard(pw);
        setTimeout(() => setOrdersBanner(null), 8000);
      })
      .catch(() => {
        setOrdersBanner("שגיאה בעדכון ההזמנות - נסה שוב");
        setTimeout(() => setOrdersBanner(null), 8000);
      })
      .finally(() => setRefreshingOrders(false));
  };
  const submitPassword = () => {
    if (!pwInput) return;
    sessionStorage.setItem("dekel_pw", pwInput);
    setAuthed(true);
    setPwError("");
    loadDashboard(pwInput);
  };

  if (!authed) {
    return (
      <div style={{ maxWidth: 360, margin: "80px auto", textAlign: "center" }}>
        <div style={card}>
          <div style={{ color: C.navy, fontSize: 20, fontWeight: "bold", marginBottom: 6 }}>עסק חכם</div>
          <div style={{ color: C.subtext, fontSize: 14, marginBottom: 20 }}>הזן סיסמה כדי להיכנס</div>
          <input
            type="password"
            value={pwInput}
            onChange={e => setPwInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitPassword(); }}
            placeholder="סיסמה"
            style={{ width: "100%", padding: "10px 12px", fontSize: 15, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 12, boxSizing: "border-box" }}
          />
          {pwError && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{pwError}</div>}
          <button
            onClick={submitPassword}
            style={{ width: "100%", padding: "10px", background: C.navy, color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: "bold", cursor: "pointer" }}
          >כניסה</button>
        </div>
      </div>
    );
  }

  if (loading) return <p style={{ textAlign: "center", marginTop: 60, color: C.navy, fontSize: 16 }}>טוען נתונים מ-SAP...</p>;
  if (!data) return <p style={{ textAlign: "center", marginTop: 60, color: C.red }}>שגיאה בטעינת נתונים</p>;

  const reorder = data.reorder || {};
  const alerts = [...(reorder.alerts || [])].sort((a, b) =>
    String(a.subgroup_name || a.subgroup || "").localeCompare(String(b.subgroup_name || b.subgroup || ""), "he")
  );
  const holidayReminders = data.holiday_reminders || [];
  const piNotShipped = data.pi_not_shipped || [];
  const deadStockItems = data.dead_stock?.items || [];
  const slowMovers = data.slow_moving || [];
  const spikes = data.spikes_by_subgroup || {};

  const tabs = [
    { id: "overview", label: "סקירה כללית" },
    { id: "order_now", label: `הזמן עכשיו (${Object.keys(reorder.order_now || {}).length})` },
    { id: "order_soon", label: `הזמן בקרוב (${Object.keys(reorder.order_soon || {}).length})` },
    { id: "no_order", label: `אל תזמין (${Object.keys(reorder.no_order || {}).length})` },
    { id: "slow_moving", label: `מלאי תקוע (${data.dead_stock?.dead_stock_items || 0})` },
    { id: "slow_movers", label: `מוצר איטי (${slowMovers.length})` },
    { id: "spikes", label: `Spike (${Object.keys(spikes).length})` },
  ];

  return (
    <div dir="rtl">
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .layout { display: flex; min-height: 100vh; }
        .sidebar { width: 200px; flex-shrink: 0; background: #4A4031; padding: 20px 12px;
          position: sticky; top: 0; height: 100vh; display: flex; flex-direction: column; gap: 6px; overflow-y: auto; }
        .content { flex: 1; padding: 20px 24px 50px; min-width: 0; max-width: 1000px; }
        .hamburger { display: none; }
        .overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 40; }
        .navItem { display: flex; align-items: center; justify-content: space-between; padding: 11px 13px;
          border-radius: 10px; cursor: pointer; color: #cdd8ea; font-weight: 600; font-size: 14px;
          border: none; background: transparent; width: 100%; text-align: right; }
        .navItem:hover { background: rgba(255,255,255,.08); color: #fff; }
        .navItem.active { background: #fff; color: #4A4031; }
        @media (max-width: 820px) {
          .sidebar { position: fixed; right: 0; left: auto; top: 0; height: 100vh; z-index: 50; background: #4A4031;
            transform: translateX(100%); transition: transform .25s ease;
            box-shadow: -8px 0 30px rgba(0,0,0,.25); direction: ltr; }
          .sidebar > * { direction: rtl; }
          .sidebar.open { transform: translateX(0); }
          .content { padding: 64px 14px 80px; overflow-x: hidden; }
          .hamburger { display: inline-flex; position: fixed; top: 12px; right: 12px; z-index: 45;
            box-shadow: 0 2px 10px rgba(0,0,0,.3); }
          .overlay.show { display: block; }
        }
      `}</style>
      <div className="layout">
        <aside className={"sidebar" + (drawerOpen ? " open" : "")}>
          <div style={{ marginBottom: 14, padding: "0 4px" }}>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 19 }}>עסק חכם</div>
            <div style={{ color: "#6B8E4E", fontSize: 12, fontWeight: 600 }}>דקל בע"מ</div>
          </div>
          {tabs.map(t => (
            <button key={t.id} className={"navItem" + (activeTab === t.id ? " active" : "")}
              onClick={() => {
                setActiveTab(t.id); setDrawerOpen(false); setShowInfo(false);
                if (t.id !== "overview") {
                  setTimeout(() => {
                    const el = document.getElementById("tab-content-top");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 80);
                }
              }}>
              {t.label}
            </button>
          ))}
        </aside>
        <div className={"overlay" + (drawerOpen ? " show" : "")} onClick={() => setDrawerOpen(false)} />
        <main className="content">
          <button className="hamburger" onClick={() => setDrawerOpen(true)}
            style={{ background: "#4A4031", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 18, cursor: "pointer", marginBottom: 14 }}>
            ☰ תפריט
          </button>

      {holidayReminders.length > 0 && (
        <div style={{ ...card, background: C.lightGold, border: `1px solid ${C.gold}` }}>
          <div style={{ color: C.navy, fontWeight: "bold", marginBottom: 10, fontSize: 15 }}>תזכורת חגים</div>
          {holidayReminders.map((h, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < holidayReminders.length - 1 ? `1px solid ${C.gold}` : "none" }}>
              <span style={{ fontSize: 18 }}>📅</span>
              <span style={{ color: C.text, fontSize: 14 }}>{h.name} בעוד {h.days_until} ימים — האם כל ההזמנות נשלחו?</span>
            </div>
          ))}
        </div>
      )}
      {piNotShipped.length > 0 && (
        <div style={{ ...card, background: "#FFF4E6", border: "1px solid #E67E22" }}>
          <div style={{ color: "#B5651D", fontWeight: "bold", marginBottom: 10, fontSize: 15 }}>הוזמן אך טרם נשלח</div>
          {piNotShipped.map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < piNotShipped.length - 1 ? "1px solid #F0C896" : "none" }}>
              <span style={{ fontSize: 18 }}>📦</span>
              <span style={{ color: C.text, fontSize: 14 }}>
                פרופורמה <bdi>{p.pi_number}</bdi> — {p.item_count} {p.item_count === 1 ? "מק\"ט טרם נשלח" : "מק\"טים טרם נשלחו"} (<bdi>{(p.items || []).join(", ")}</bdi>), הוזמנה לפני {p.days_since} ימים
              </span>
              <button
                onClick={() => handleConfirmClosure(p.pi_number)}
                disabled={closingPi === p.pi_number}
                title="סמן שההזמנה הגיעה וסגור אותה"
                style={{
                  marginRight: "auto", flexShrink: 0, cursor: "pointer",
                  fontSize: 12, padding: "4px 10px", borderRadius: 6,
                  border: "1px solid #C0A080", background: "#fff", color: "#8A6D3B",
                  opacity: closingPi === p.pi_number ? 0.5 : 1,
                }}>
                {closingPi === p.pi_number ? "סוגר..." : "✓ הגיעה"}
              </button>
            </div>
          ))}
        </div>
      )}
      {alerts.length > 0 && (
        <div style={{ ...card, background: "#FEF2F2", border: `1px solid ${C.red}` }}>
          <div style={{ color: C.red, fontWeight: "bold", marginBottom: 10, fontSize: 15 }}>התראות דחופות</div>
          {alerts.map((a, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < alerts.length - 1 ? `1px solid #fca5a5` : "none" }}>
              <span style={{ color: C.red, fontWeight: "bold", fontSize: 18 }}>!</span>
              <span style={{ color: C.text, fontSize: 14 }}>
                {a.subgroup_name} ({a.subgroup}) – {a.count} מוצרים דורשים הזמנה
                {a.already_ordered > 0 && ` (${a.already_ordered} כבר הוזמנו)`}
              </span>
            </div>
          ))}
        </div>
      )}


          {TAB_INFO[activeTab] && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "#4A4031" }}>
                {tabs.find(t => t.id === activeTab)?.label.replace(/\s*\(\d+\)\s*$/, "")}
              </h2>
              <button onClick={() => setShowInfo(v => !v)} title="מה זה?"
                style={{ width: 24, height: 24, borderRadius: "50%", border: "1.5px solid #4A4031",
                  background: showInfo ? "#4A4031" : "#fff", color: showInfo ? "#fff" : "#4A4031",
                  fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "Georgia, serif", lineHeight: 1 }}>i</button>
              {["order_now", "order_soon", "no_order"].includes(activeTab) && (
                <button onClick={handleRefreshOrders} disabled={refreshingOrders}
                  style={{ marginInlineStart: "auto", display: "inline-flex", alignItems: "center", gap: 6,
                    background: "#fff", color: refreshingOrders ? "#9aa3b2" : "#4A4031",
                    border: `1.5px solid ${refreshingOrders ? "#cdd5e0" : "#4A4031"}`,
                    borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 700,
                    cursor: refreshingOrders ? "default" : "pointer", whiteSpace: "nowrap" }}>
                  <span style={{ display: "inline-block", transformOrigin: "center",
                    animation: refreshingOrders ? "spin 1s linear infinite" : "none" }}>🔄</span>
                  {refreshingOrders ? "מרענן..." : "רענן הזמנות"}
                </button>
              )}
            </div>
          )}
          {showInfo && TAB_INFO[activeTab] && (
            <div style={{ background: "#EEF3FB", border: "1px solid #D4E0F2", borderRadius: 10, padding: "13px 16px", marginBottom: 16, color: "#2c4a73", fontSize: 14, lineHeight: 1.6 }}>
              {TAB_INFO[activeTab]}
            </div>
          )}
          {ordersBanner && ["order_now", "order_soon", "no_order"].includes(activeTab) && (
            <div style={{ marginBottom: 14, background: "#ECFDF3", border: "1px solid #A6E9C5", borderRadius: 8,
              padding: "10px 14px", color: "#1a7a4a", fontSize: 14, fontWeight: 600 }}>
              ✓ {ordersBanner}
            </div>
          )}
      <div id="tab-content-top" style={{ scrollMarginTop: 16 }} />
      <GrowthOpportunities items={growthItems} onMark={markGrowth} busy={growthBusy} leaving={growthLeaving} />
      {activeTab === "overview" && (
        <>
          <div style={card}>
            <div style={titleStyle}><span>חיפוש מוצר (מק"ט או שם)</span></div>
            <input
              value={gSearch}
              onChange={e => setGSearch(e.target.value)}
              placeholder='הקלד מק"ט או שם מוצר...'
              style={{ width: "100%", padding: "10px 12px", fontSize: 15, borderRadius: 8,
                       border: "1px solid #ddd", boxSizing: "border-box" }}
            />
            {gResults.length > 0 && (
              <div style={{ marginTop: 8, border: "1px solid #eee", borderRadius: 8, overflow: "hidden" }}>
                {gResults.map((r, i) => (
                  <div key={r.item_code} onClick={() => loadProduct(r.item_code)}
                    style={{ padding: "8px 12px", cursor: "pointer",
                             borderBottom: i < gResults.length - 1 ? "1px solid #f0f0f0" : "none",
                             display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ color: C.text, fontSize: 14 }}><bdi>{r.item_code}</bdi> — {r.name}</span>
                    <span style={{ color: C.subtext, fontSize: 12, flexShrink: 0 }}>מלאי {r.stock}</span>
                  </div>
                ))}
              </div>
            )}
            {gLoading && <div style={{ marginTop: 10, color: C.subtext }}>טוען...</div>}
            {gProduct && gProduct.found === false && (
              <div style={{ marginTop: 10, color: "#B5651D" }}>לא נמצא מוצר עם המק"ט הזה</div>
            )}
            {gProduct && gProduct.found && (
              <div style={{ marginTop: 12, padding: 14, background: "#FAFAFA", borderRadius: 8, border: "1px solid #eee" }}>
                <div style={{ fontWeight: "bold", fontSize: 15, color: C.text, marginBottom: 4 }}>
                  <bdi>{gProduct.item_code}</bdi> — {gProduct.name}
                </div>
                <div style={{ color: C.subtext, fontSize: 13, marginBottom: 10 }}>
                  {gProduct.subgroup_name} ({gProduct.subgroup})
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: 14 }}>
                  <span>סטטוס: <b>{gProduct.status}</b></span>
                  <span>מלאי: <b>{gProduct.stock}</b></span>
                  {gProduct.days_left != null && <span>ימים שנותרו: <b>{gProduct.days_left}</b></span>}
                  {gProduct.daily_sales != null && <span>קצב יומי: <b>{gProduct.daily_sales}</b></span>}
                  {gProduct.recommended_qty != null && <span>המלצת הזמנה: <b>{gProduct.recommended_qty}</b></span>}
                  {gProduct.is_final && <span style={{ color: "#B5651D" }}>פריט סופני</span>}
                  {gProduct.cny_early && <span style={{ color: "#B5651D" }}>🎎 הזמנה מוקדמת - ראש השנה הסיני</span>}
                </div>
                {gProduct.incoming && (
                  <div style={{ marginTop: 8, fontSize: 13, color: C.navy }}>
                    🚚 בדרך: {(gProduct.incoming.open_qty || 0).toLocaleString()} יח' {(gProduct.incoming.orders || []).length > 1
                      ? "— " + gProduct.incoming.orders.map(o => `הזמנה ${o.doc_num}: ${(o.open_qty || 0).toLocaleString()} יח' (${o.date})`).join(" | ")
                      : `(הזמנה #${gProduct.incoming.doc_num})`}
                  </div>
                )}
                {gProduct.pi_ordered && (
                  <div style={{ marginTop: 4, fontSize: 13, color: C.navy }}>
                    🧾 הוזמן: {(gProduct.pi_ordered.pi_qty || 0).toLocaleString()} יח'
                    {(gProduct.pi_ordered.open_pis || []).map(p => ` (${p.pi_number})`).join("")}
                  </div>
                )}
                {gProduct.spike_buffer != null && (
                  <div style={{ marginTop: 4, fontSize: 13, color: "#8A6D3B" }}>
                    ⚡ spike buffer: {gProduct.spike_buffer}
                  </div>
                )}
                {gProduct.last_sale && (
                  <div style={{ marginTop: 4, fontSize: 12, color: C.subtext }}>מכירה אחרונה: {gProduct.last_sale}</div>
                )}
                <div style={{ marginTop: 4, fontSize: 12, color: C.subtext }}>כניסה אחרונה: {gProduct.last_receipt || "אין נתונים"}</div>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
            {[
              { value: `₪${(data.turnover?.revenue_this_month || 0).toLocaleString()}`, label: "הכנסה נטו 30 יום", color: C.green },
              { value: (data.turnover?.units_sold_this_month || 0).toLocaleString(), label: "יחידות נמכרו", color: C.navy },
              { value: `${data.turnover?.revenue_growth_pct || 0}%`, label: "צמיחה לעומת 30 יום קודם", color: (data.turnover?.revenue_growth_pct || 0) >= 0 ? C.green : C.red },
            ].map((k, i) => (
              <div key={i} style={{ background: C.white, borderRadius: 12, padding: 20, textAlign: "center", border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
                <div style={{ color: C.subtext, fontSize: 13, marginTop: 6 }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={card}>
            <div style={titleStyle}><span>מכירות לפי חודש</span></div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={v => `₪${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => `₪${v.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="החודש" fill={C.navy} radius={[4, 4, 0, 0]} />
                <Bar dataKey="חודש קודם" fill={C.gold} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={card}>
            <div style={titleStyle}><span>50 המוצרים המובילים (30 יום)</span></div>
            <input
              value={topSearch}
              onChange={e => setTopSearch(e.target.value)}
              placeholder="חיפוש מק״ט או שם מוצר"
              style={{ width: "100%", padding: "9px 12px", fontSize: 14, border: "1px solid #E0E0E0", borderRadius: 8, marginBottom: 12, boxSizing: "border-box" }}
            />
            <div style={{ fontSize: 12, color: C.subtext, marginBottom: 10, lineHeight: 1.6 }}>
              <span>🚨 נכנס לרשימה בגלל הזמנה חריגה בודדת — לא בהכרח ביקוש קבוע</span><br />
              <span>🔵 הייתה הזמנה חריגה, אך המוצר נמכר טוב גם בלעדיה</span>
            </div>
            {(data.top_products || []).map((p, origIdx) => ({ ...p, origRank: origIdx })).filter(p => {
              const q = topSearch.trim().toLowerCase();
              if (!q) return true;
              return String(p.code || "").toLowerCase().includes(q) || String(p.name || "").toLowerCase().includes(q);
            }).map((p) => {
              const i = p.origRank;
              return (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < (data.top_products.length - 1) ? `1px solid ${C.border}` : "none", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                  <div style={{ width: 26, height: 26, flexShrink: 0, borderRadius: "50%", background: i === 0 ? C.gold : C.navy, color: i === 0 ? C.navy : C.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: "bold" }}>{i + 1}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: "bold", color: C.text, fontSize: 14 }}>
                      {p.spike_flag === "spike_is_reason" && <span title="נכנס לרשימה בגלל הזמנה חריגה בודדת">🚨 </span>}
                      {p.spike_flag === "has_spike_still_strong" && <span title="הייתה הזמנה חריגה אך המוצר חזק גם בלעדיה">🔵 </span>}
                      {p.code} — {p.name}
                    </div>
                    <div style={{ color: C.subtext, fontSize: 12 }}>{p.subgroup_name} ({p.subgroup})</div>
                  </div>
                </div>
                <div style={{ textAlign: "left", flexShrink: 0 }}>
                  <div style={{ color: C.green, fontWeight: "bold", fontSize: 14 }}>₪{(p.turnover || 0).toLocaleString()}</div>
                  <div style={{ color: C.subtext, fontSize: 11 }}>
                    {p.has_cost ? `רווח ${p.profit_pct}%` : "אין נתוני עלות"}
                  </div>
                  <div style={{ color: C.subtext, fontSize: 11 }}>מלאי: {(p.on_hand || 0).toLocaleString()} | 30 יום: {(p.qty_30 || 0).toLocaleString()} | 90 יום: {(p.qty_90 || 0).toLocaleString()}</div>
                  <div style={{ color: C.subtext, fontSize: 11 }}>כניסה אחרונה: {p.last_receipt || "אין נתונים"}</div>
                </div>
              </div>
            ); })}
          </div>

          <SubgroupSales groups={sgData} />
          <div style={{ ...card, background: C.lightGold, border: `1px solid ${C.gold}` }}>
            <div style={{ color: C.navy, fontWeight: "bold", marginBottom: 6 }}>סיכום</div>
            <p style={{ margin: 0, color: C.text, lineHeight: 1.6 }}>{data.summary}</p>
          </div>
        </>
      )}


      {activeTab === "order_now" && (
        <SubgroupSection sectionTitle="הזמן עכשיו – פחות מ-120 יום" data={reorder.order_now} color={C.red} columns={reorderCols} />
      )}

      {activeTab === "order_soon" && (
        <SubgroupSection sectionTitle="הזמן בקרוב – 120-150 יום" data={reorder.order_soon} color={C.orange} columns={reorderCols} />
      )}

      {activeTab === "no_order" && (
        <SubgroupSection sectionTitle="אל תזמין – עומד להגמר אבל נמכר לאט" data={reorder.no_order} color={C.navy} columns={reorderCols} noOrderLabel={true} />
      )}

      {activeTab === "slow_moving" && (
        <div style={card}>
          <div style={titleStyle}>
            <span>מלאי תקוע (מכר פחות מ-10% ב-6 חודשים)</span>
            <ExcelBtn onClick={() => downloadCSV(deadStockItems, "מלאי_תקוע.csv", deadStockCols)} />
          </div>
          <div style={{ background: "#FEF2F2", borderRadius: 8, padding: "12px 16px", marginBottom: 14, display: "flex", gap: 16, alignItems: "center" }}>
            <span style={{ color: C.red, fontWeight: 800, fontSize: 22 }}>{data.dead_stock?.dead_stock_items || 0}</span>
            <span style={{ color: C.subtext }}>פריטים בשווי</span>
            <span style={{ color: C.red, fontWeight: "bold" }}>₪{(data.dead_stock?.dead_stock_value || 0).toLocaleString()}</span>
          </div>
          <ListControls search={deadSearch} setSearch={setDeadSearch} sortBy={deadSort} setSortBy={setDeadSort}
            sortOptions={[{key:"value",label:"שווי"},{key:"stock",label:"מלאי"},{key:"code",label:"מק״ט"}]} />
          {[...deadStockItems]
            .filter(p => {
              const q = deadSearch.trim().toLowerCase();
              if (!q) return true;
              return String(p.ItemCode || "").toLowerCase().includes(q) || String(p.ItemName || "").toLowerCase().includes(q);
            })
            .sort((a, b) => {
              if (deadSort === "code") return String(a.ItemCode).localeCompare(String(b.ItemCode));
              if (deadSort === "stock") return (b.OnHand || 0) - (a.OnHand || 0);
              return (b.StockValue || 0) - (a.StockValue || 0); // value (default)
            })
            .map((p, i, arr) => (
            <div key={p.ItemCode || i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 13, flexWrap: "wrap", gap: 4 }}>
              <span style={{ fontWeight: "bold", color: C.text, minWidth: 70 }}>{p.ItemCode}</span>
              <span style={{ color: C.subtext, flex: 1, minWidth: 120 }}>{p.ItemName}</span>
              <span style={{ color: C.subtext }}>{p.U_SubGroup}</span>
              <span style={{ color: C.subtext }}>{(p.OnHand || 0).toLocaleString()} יח'</span>
              <span style={{ color: C.red, fontWeight: "bold" }}>₪{(p.StockValue || 0).toLocaleString()}</span>
              <span style={{ color: C.subtext, fontSize: 12 }}>כניסה אחרונה: {p.last_receipt || "אין נתונים"}</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === "slow_movers" && (
        <div style={card}>
          <div style={titleStyle}>
            <span>מוצר איטי (מכר פחות מ-25% ברבעון)</span>
            <ExcelBtn onClick={() => downloadCSV(slowMovers, "מוצר_איטי.csv", deadStockCols)} />
          </div>
          <div style={{ background: C.lightGold, borderRadius: 8, padding: "12px 16px", marginBottom: 14, display: "flex", gap: 16, alignItems: "center" }}>
            <span style={{ color: C.orange, fontWeight: 800, fontSize: 22 }}>{slowMovers.length}</span>
            <span style={{ color: C.subtext }}>מוצרים נמכרים לאט</span>
          </div>
          <ListControls search={slowSearch} setSearch={setSlowSearch} sortBy={slowSort} setSortBy={setSlowSort}
            sortOptions={[{key:"stock",label:"מלאי"},{key:"quarter",label:"מכר ברבעון"},{key:"code",label:"מק״ט"}]} />
          {[...slowMovers]
            .filter(p => {
              const q = slowSearch.trim().toLowerCase();
              if (!q) return true;
              return String(p.ItemCode || "").toLowerCase().includes(q) || String(p.ItemName || "").toLowerCase().includes(q);
            })
            .sort((a, b) => {
              if (slowSort === "code") return String(a.ItemCode).localeCompare(String(b.ItemCode));
              if (slowSort === "quarter") return (b.SoldLast3M || 0) - (a.SoldLast3M || 0);
              return (b.OnHand || 0) - (a.OnHand || 0); // stock (default)
            })
            .map((p, i, arr) => (
            <div key={p.ItemCode || i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 13, flexWrap: "wrap", gap: 4 }}>
              <span style={{ fontWeight: "bold", color: C.text, minWidth: 70 }}>{p.ItemCode}</span>
              <span style={{ color: C.subtext, flex: 1, minWidth: 120 }}>{p.ItemName}</span>
              <span style={{ color: C.subtext }}>{p.subgroup_name} ({p.U_SubGroup})</span>
              <span style={{ color: C.subtext }}>מלאי: {(p.OnHand || 0).toLocaleString()}</span>
              <span style={{ color: C.orange, fontWeight: "bold" }}>מכר ברבעון: {(p.SoldLast3M || 0).toLocaleString()}</span>
              <span style={{ color: C.subtext, fontSize: 12 }}>כניסה אחרונה: {p.last_receipt || "אין נתונים"}</span>
            </div>
          ))}
        </div>
      )}
      {activeTab === "spikes" && <SpikeSection data={spikes} />}

      <p style={{ color: "#aaa", fontSize: 11, textAlign: "center" }}>
        עודכן: {new Date(data.generated_at).toLocaleString("he-IL")}
      </p>
        </main>
      </div>
    </div>
  );
}


function SubgroupSales({ groups }) {
  const [open, setOpen] = useState({});
  const [sort, setSort] = useState("revenue");
  if (!groups || groups.length === 0) return null;
  const sorted = [...groups].sort((a, b) =>
    sort === "alpha"
      ? (a.subgroup_name || "").localeCompare(b.subgroup_name || "", "he")
      : b.total_net - a.total_net);
  const fmt = (n) => (n || 0).toLocaleString("he-IL");
  const btn = (on) => ({
    padding: "6px 12px", borderRadius: 8, cursor: "pointer",
    border: "1px solid #4A4031", background: on ? "#4A4031" : "#fff",
    color: on ? "#fff" : "#4A4031", fontWeight: 600, fontSize: 13,
  });
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontWeight: "bold", fontSize: 16 }}>מכירות לפי תת-קבוצה (מתחילת השנה)</span>
        <span style={{ flex: 1 }} />
        <button onClick={() => setSort("revenue")} style={btn(sort === "revenue")}>לפי מחזור</button>
        <button onClick={() => setSort("alpha")} style={btn(sort === "alpha")}>לפי א-ב</button>
      </div>
      {sorted.map((g) => (
        <div key={g.subgroup} style={{ borderBottom: "1px solid #eee" }}>
          <div onClick={() => setOpen((s) => ({ ...s, [g.subgroup]: !s[g.subgroup] }))}
               style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "10px 4px", cursor: "pointer" }}>
            <span style={{ fontWeight: 600 }}>{open[g.subgroup] ? "\u25BC" : "\u25C0"} {g.subgroup_name}</span>
            <span style={{ fontWeight: "bold", color: "#6B8E4E" }}>{"\u20AA"}{fmt(g.total_net)}</span>
          </div>
          {open[g.subgroup] && (
            <div style={{ padding: "0 4px 12px", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ color: "#888", textAlign: "right" }}>
                    <th style={{ padding: "5px 6px" }}>{"\u05DE\u05E7\u05F4\u05D8"}</th>
                    <th style={{ padding: "5px 6px" }}>שם מוצר</th>
                    <th style={{ padding: "5px 6px" }}>מחזור שנתי</th>
                    <th style={{ padding: "5px 6px" }}>כמות</th>
                    <th style={{ padding: "5px 6px" }}>מארז</th>
                  </tr>
                </thead>
                <tbody>
                  {g.products.map((p) => (
                    <tr key={p.code} style={{ borderTop: "1px solid #f2f2f2" }}>
                      <td style={{ padding: "5px 6px", fontWeight: 600 }}>{p.code}</td>
                      <td style={{ padding: "5px 6px" }}>{p.name}</td>
                      <td style={{ padding: "5px 6px", fontWeight: 600 }}>{"\u20AA"}{fmt(p.net_ytd)}</td>
                      <td style={{ padding: "5px 6px" }}>{fmt(p.qty_ytd)}</td>
                      <td style={{ padding: "5px 6px" }}>{p.pack_qty > 1 ? fmt(p.pack_qty) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
